<template>
  <Modal>
    <Modal.Title>Remove access?</Modal.Title>
    <Modal.Body>
      Are you sure you want to remove {{ user }}
      access to this file?
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="loading"
        @click="close()"
      >
        Cancel
      </Button>
      <Button
        variant="danger"
        :size="isMobile ? 'lg' : 'md'"
        :loading="loading"
        @click="handleClick"
      >
        Remove
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Modal, Button, useModal, useSheet } from '@dacr/library'
import { graphql } from '@/shared/graphql/router'
import {
  InviteType,
  type CoOwnersConnection,
  type InviteInput,
} from '@/shared/graphql/router/graphql'
import type { Reference } from '@apollo/client'
import { useMutation } from '@vue/apollo-composable'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'

const REMOVE_CO_OWNER_MUTATION = graphql(`
  mutation RemoveCoOwner($input: RemoveCoOwnerInput!) {
    removeCoOwner(input: $input) {
      success
    }
  }
`)

const { close, closeAll } = useModal()
const { closeAll: closeAllSheets } = useSheet()
const { isMobile } = useDevice()

const props = defineProps<{
  fileId: string
  invite: InviteInput
  name: string
}>()

const { mutate: removeOwner, loading } = useMutation(REMOVE_CO_OWNER_MUTATION, {
  clientId: 'router',
})

const toastStore = useToastStore()
const userStore = useUserStore()

const user = computed(() =>
  props.invite.id === userStore.user?.id ? 'your' : `${props.name}'s`
)

const handleClick = async () => {
  try {
    await removeOwner(
      { input: { fileId: props.fileId, invite: props.invite } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.fileId }),
            fields: {
              coOwners(existing, { readField }) {
                const data = existing as CoOwnersConnection
                if (!data?.edges) return existing

                return {
                  ...existing,
                  edges: data.edges.filter(({ node }) => {
                    if (props.invite.type === InviteType.Email) {
                      return readField('email', node) !== props.invite.email
                    }

                    const entity = readField<Reference>('entity', node)
                    return entity
                      ? readField('id', entity) !== props.invite.id
                      : true
                  }),
                }
              },
            },
          })
        },
      }
    )

    if (props.invite.id === userStore.user?.id) {
      closeAll()
      closeAllSheets()
      return
    }

    close()
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not remove owner',
      error: error as Error,
    })
  }
}
</script>
